import { collection, getDocs, orderBy, query } from "firebase/firestore";

import { db } from "integrations/firebase/firestore";

import { summariseSignals } from "../event-experience/directory-filters";
import {
  directoryAttendeeSchema,
  signalSchema,
  signalSelectionSchema,
  type DirectoryAttendee,
  type Signal,
  type SignalSelection,
} from "../event-experience/schemas";
import {
  resolveSignals,
  sortSignals,
  type ResolvedSignal,
} from "../event-experience/signal-utils";

/**
 * The content of the signals summary PDF (TNG-96): each signal, how many
 * picked it, and who, with role and company.
 *
 * Built from the directory, through summariseSignals, so it counts exactly
 * the people the Attendees tab would. A withdrawn or deleted person has left
 * the directory, so they never appear, even though their selection document
 * outlives them.
 *
 * Only name, role and company are copied out of a directory entry. The PDF
 * never carries contact details, whatever the event's attendee switch says:
 * a file, once shared, cannot be taken back.
 */

export interface SummaryPerson {
  name: string;
  role: string;
  company: string;
}

export interface SummarySignal {
  label: string;
  count: number;
  people: SummaryPerson[];
}

export function signalsSummaryRows(
  signals: readonly Signal[],
  directory: readonly DirectoryAttendee[],
  selections: readonly SignalSelection[],
): SummarySignal[] {
  const ordered = sortSignals([...signals]);
  const signalsByAttendee = new Map<string, ResolvedSignal[]>(
    selections.map((selection) => [
      selection.id,
      resolveSignals(ordered, selection.signalIds),
    ]),
  );
  return summariseSignals(ordered, directory, signalsByAttendee).map(
    ({ signal, attendees }) => ({
      label: signal.label,
      count: attendees.length,
      people: attendees.map((attendee) => ({
        name: attendee.name,
        role: attendee.role,
        company: attendee.company,
      })),
    }),
  );
}

function parseAll<T>(
  docs: { id: string; data: () => unknown }[],
  schema: { safeParse: (value: unknown) => { success: boolean; data?: T } },
): T[] {
  return docs.flatMap((item) => {
    const result = schema.safeParse({
      id: item.id,
      ...(item.data() as object),
    });
    return result.success && result.data !== undefined ? [result.data] : [];
  });
}

/**
 * Reads what the summary needs, once, when the button is tapped: the three
 * collections every member may read. Not kept live, since the PDF is a
 * snapshot anyway.
 */
export async function loadSignalsSummary(
  eventId: string,
): Promise<SummarySignal[]> {
  const [signals, directory, selections] = await Promise.all([
    getDocs(collection(db, "events", eventId, "signals")),
    getDocs(
      query(collection(db, "events", eventId, "directory"), orderBy("name")),
    ),
    getDocs(collection(db, "events", eventId, "signal_selections")),
  ]);
  return signalsSummaryRows(
    parseAll<Signal>(signals.docs, signalSchema),
    parseAll<DirectoryAttendee>(directory.docs, directoryAttendeeSchema),
    parseAll<SignalSelection>(selections.docs, signalSelectionSchema),
  );
}

/** A file name that is safe on every platform: letters, digits and dashes. */
export function summaryFileName(eventTitle: string): string {
  const slug = eventTitle
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${slug || "event"}-signals-summary.pdf`;
}
